import { queryOptions, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

import { useI18n } from "@/core/i18n";

import { getPlatformCurrent, getStorefrontMetadata } from "api/methods";
import { useBrowseLocationId } from "modules/anonymousBrowsing/anonymousLocation";
import { isPublicStorefrontRoute } from "modules/storefront/publicStorefrontRoute";
import { isNativeMobileApp } from "utils/platform.helpers";

const Favicon = () => {
  const location = useLocation();
  const { currentLang } = useI18n();
  // Match the backend SupportedLanguages culture mapping; unsupported cultures use source English.
  const languageCode =
    currentLang === "es-MX"
      ? 1
      : currentLang === "fr-CA"
        ? 2
        : currentLang === "en-CA"
          ? 3
          : currentLang === "en-GB"
            ? 5
            : 0;
  const browseLocation = useBrowseLocationId();
  const promotion = new URLSearchParams(location.search).get("promotion");
  const {
    data: metadata,
    isError,
    isFetching,
  } = useQuery({
    ...getStorefrontMetadata.keyed({
      path: location.pathname,
      promotion: promotion ?? undefined,
      languageCode,
      browseLocation: browseLocation ?? undefined,
    }),
    enabled: !isNativeMobileApp(),
    staleTime: 0,
  });
  const publicRoute = isPublicStorefrontRoute(location.pathname, location.search);
  const awaitingPublicMetadata = publicRoute && isFetching && !isError && !isNativeMobileApp();
  const published = metadata?.indexable === true && !isFetching && !isError && publicRoute;
  const { data: platform } = useQuery(
    queryOptions({
      queryKey: getPlatformCurrent.key(),
      queryFn: () =>
        getPlatformCurrent().catch((error: unknown) => {
          // Only a server answer means this host has no practice. A request the browser cancelled
          // has no response: iOS Safari cancels it when the save-contact page opens the vCard, and
          // the page stays open under Add Contact.
          if (!isNativeMobileApp() && isAxiosError(error) && error.response !== undefined) {
            window.location.href = "https://www.repeatmd.com";
          }
          throw error;
        }),
      staleTime: 0,
    }),
  );

  return (
    <Helmet>
      <title>{metadata ? metadata.title.text : platform?.name}</title>
      {!awaitingPublicMetadata && (
        <meta name="robots" content={published ? "index, follow" : "noindex, nofollow"} />
      )}
      <meta name="description" content={metadata ? metadata.description.text : ""} />
      {published && metadata.canonicalUrl && <link rel="canonical" href={metadata.canonicalUrl} />}
      {published && metadata.canonicalUrl && (
        <meta property="og:url" content={metadata.canonicalUrl} />
      )}
      {published && <meta property="og:title" content={metadata.title.text} />}
      {published && <meta property="og:description" content={metadata.description.text} />}
      {published && <meta property="og:type" content="website" />}
      {published && <meta name="twitter:card" content="summary_large_image" />}
      {published && <meta name="twitter:title" content={metadata.title.text} />}
      {published && <meta name="twitter:description" content={metadata.description.text} />}
      {published && metadata.imageUrl && <meta property="og:image" content={metadata.imageUrl} />}
      {published && metadata.imageUrl && <meta name="twitter:image" content={metadata.imageUrl} />}
      {published && metadata.structuredDataJson && (
        <script type="application/ld+json">{metadata.structuredDataJson}</script>
      )}
      <link rel="icon" href={platform?.faviconUrl} />
      <link rel="apple-touch-icon" href={platform?.faviconUrl} />
    </Helmet>
  );
};

export default Favicon;
